import { t } from '../../i18n';
// src/components/ads/AdPreview.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { computeRuntimeStatus } from './adsStorage';
import { AdLayoutCanvas } from './AdLayoutEditor';
import { AD_LANGUAGES, getProfilesForDevice, normalizeContentJson, normalizeLayoutJson } from './adLayoutDefaults';

function Badge({ tone = 'gray', children }) {
  const cls = tone === 'green'
    ? 'bg-green-50 text-green-700 border-green-200'
    : tone === 'amber'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : tone === 'red'
        ? 'bg-red-50 text-red-700 border-red-200'
        : 'bg-gray-100 text-gray-700 border-gray-200';
  return <span className={`text-xs px-2 py-0.5 rounded border ${cls}`}>{children}</span>;
}

function statusTone(st) {
  if (st === 'ACTIVE') return 'green';
  if (st === 'SCHEDULED' || st === 'PENDING') return 'amber';
  if (st === 'EXPIRED') return 'red';
  return 'gray';
}

function statusLabel(st) {
  if (st === 'ACTIVE') return t('노출중');
  if (st === 'SCHEDULED') return t('예약');
  if (st === 'PENDING') return t('예약 보류');
  if (st === 'EXPIRED') return t('만료');
  if (st === 'DISABLED') return t('비활성');
  return st;
}

function fmtDT(v) {
  if (!v) return '-';
  try {
    const d = new Date(String(v).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return v;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return v;
  }
}

export default function AdPreview({ ad, onEdit, onDelete }) {
  const st = useMemo(() => computeRuntimeStatus(ad), [ad]);
  const [previewDevice, setPreviewDevice] = useState(ad?.device === 'mobile' ? 'mobile' : 'desktop');
  const device = ad?.device === 'responsive' ? previewDevice : (ad?.device === 'mobile' ? 'mobile' : 'desktop');
  const profiles = useMemo(() => getProfilesForDevice(device), [device]);
  const contentJson = useMemo(() => normalizeContentJson(ad?.contentJson), [ad?.contentJson]);
  const layoutJson = useMemo(() => normalizeLayoutJson(ad?.layoutJson), [ad?.layoutJson]);
  const [profile, setProfile] = useState(profiles[0].value);
  const [language, setLanguage] = useState(contentJson.defaultLanguage || 'ko');

  useEffect(() => {
    setPreviewDevice(ad?.device === 'mobile' ? 'mobile' : 'desktop');
  }, [ad?.id, ad?.device]);

  useEffect(() => {
    setProfile(getProfilesForDevice(device)[0].value);
  }, [device]);

  useEffect(() => {
    setLanguage(contentJson.defaultLanguage || 'ko');
  }, [ad?.id, contentJson.defaultLanguage]);

  if (!ad) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-10 text-center text-gray-500">
        {t('왼쪽에서 광고를 선택해줘.')}
      </div>
    );
  }

  const openLink = () => {
    if (!ad.clickUrl) return;
    window.open(ad.clickUrl, '_blank', 'noopener,noreferrer');
  };

  const previewImageUrl = device === 'mobile' ? (ad.mobileImageUrl || ad.imageUrl) : (ad.desktopImageUrl || ad.imageUrl);
  const openImage = () => {
    if (!previewImageUrl) return;
    window.open(previewImageUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="p-4 sm:p-5 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">{ad.label || t('(이름없음)')}</div>

          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <Badge tone={statusTone(st)}>{statusLabel(st)}</Badge>
            <Badge>area: {ad.area}</Badge>
            <Badge>{ad.device}</Badge>
            <Badge>{ad.country}</Badge>
            <Badge>order: {ad.order ?? 100}</Badge>
          </div>

          <div className="mt-3 text-sm text-gray-700 break-all">
            {t('링크:')} <span className="text-gray-900">{ad.clickUrl || t('(없음)')}</span>
            {ad.clickUrl ? (
              <button onClick={openLink} className="ml-2 text-xs px-2 py-1 rounded border border-gray-200 hover:border-gray-400">
                {t('링크 열기')}
              </button>
            ) : null}
          </div>

          <div className="mt-2 text-xs text-gray-500">
            start: {fmtDT(ad.startAt)} · end: {fmtDT(ad.endAt)}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={onEdit} className="px-3 py-2 rounded border border-gray-200 hover:border-gray-400">{t('수정')}</button>
          <button onClick={onDelete} className="px-3 py-2 rounded bg-red-500 text-white hover:bg-red-600">{t('삭제')}</button>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="text-sm font-semibold">{t('실제 비율 미리보기')}</div>
            {previewImageUrl ? (
              <button onClick={openImage} className="text-xs px-2 py-1 rounded border border-gray-200 hover:border-gray-400">
                {t('이미지 열기')}
              </button>
            ) : null}
          </div>

          <div className="p-3 sm:p-4 bg-white">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {ad.device === 'responsive' ? (
                <select value={previewDevice} onChange={(e) => setPreviewDevice(e.target.value)} className="px-2 py-1.5 rounded border border-gray-200 bg-white text-xs">
                  <option value="desktop">Desktop</option>
                  <option value="mobile">Mobile</option>
                </select>
              ) : null}
              <select value={profile} onChange={(e) => setProfile(e.target.value)} className="px-2 py-1.5 rounded border border-gray-200 bg-white text-xs">
                {profiles.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} className="px-2 py-1.5 rounded border border-gray-200 bg-white text-xs">
                {AD_LANGUAGES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </div>

            {previewImageUrl ? (
              <div className="max-w-full mx-auto">
                <AdLayoutCanvas
                  imageUrl={previewImageUrl}
                  contentJson={contentJson}
                  layoutJson={layoutJson}
                  device={device}
                  profile={profile}
                  language={language}
                  backgroundMode={ad.backgroundMode || 'COVER'}
                  templateKey={ad.templateKey || 'IMAGE_ONLY'}
                />
              </div>
            ) : (
              <div className="h-[160px] grid place-items-center text-sm text-gray-400">{t('등록된 이미지 없음')}</div>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded border border-gray-200 p-3">
            <div className="text-xs text-gray-400">{t('파일명')}</div>
            <div className="mt-1 break-all">{ad.originalFileName || '-'}</div>
          </div>

          <div className="rounded border border-gray-200 p-3">
            <div className="text-xs text-gray-400">{t('용량')}</div>
            <div className="mt-1">{ad.fileSize ? `${Math.round(ad.fileSize / 1024)}KB` : '-'}</div>
          </div>

          <div className="rounded border border-gray-200 p-3 sm:col-span-2">
            <div className="text-xs text-gray-400">{t('이미지 URL')}</div>
            <div className="mt-1 break-all">Desktop: {ad.desktopImageUrl || ad.imageUrl || '-'}<br />Mobile: {ad.mobileImageUrl || t('(미등록 · Desktop fallback)')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
